/**
 * Form04_VestibularFunctionConsent.jsx
 * Collection: vestibular_consent
 * Redirect: /forms/05-electronic-communications
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/vestibular-consent";
const NEXT    = "/forms/05-electronic-communications";

export default function VestibularFunctionConsent({
  patientName="", patientAge="", patientGender="", providerName="", practiceName="", onSubmit,
}) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const witnessSigRef = useRef(null);
  const techSigRef    = useRef(null);

  const [sigEmpty,        setSigEmpty]        = useState(true);
  const [witnessSigEmpty, setWitnessSigEmpty] = useState(true);
  const [techSigEmpty,    setTechSigEmpty]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    responsibleParty: patientName,
    relationship:     "Self",
    sigDate:          new Date().toISOString().split("T")[0],
    witnessName:      "",
    witnessDate:      new Date().toISOString().split("T")[0],
    techName:         "",
    techDate:         new Date().toISOString().split("T")[0],
  });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const getSig  = (ref) => (!ref.current||ref.current.isEmpty()) ? null : ref.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType:     "vestibular_consent",
    patient:      { name: patientName, age: patientAge !== "" ? Number(patientAge) : null, gender: patientGender },
    providerName, practiceName,
    consentGiven: true,
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate || null,
      signatureDataURL: getSig(sigRef),
    },
    witness: {
      name:            form.witnessName,
      date:            form.witnessDate || null,
      signatureDataURL: getSig(witnessSigRef),
    },
    diagnosticTechnician: {
      name:            form.techName,
      date:            form.techDate || null,
      signatureDataURL: getSig(techSigRef),
    },
    meta: { submittedAt: new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Patient signature required.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit==="function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    } catch(e){ console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef,`vestibular-consent-${patientName||"patient"}.pdf`); showToast("Downloaded!"); }
    catch { showToast("PDF failed.","error"); }
  };

  const SigBox = ({label, sigRef2, empty, setEmpty, nameVal, setNameVal, dateVal, setDateVal, nameLabel}) => (
    <div style={{padding:"16px",border:`1.5px solid ${C.border}`,borderRadius:10,background:"#FAFBFC"}}>
      <div style={{fontWeight:600,fontSize:13,marginBottom:12,color:C.navy}}>{label}</div>
      <div className="wf-grid wf-grid-2" style={{marginBottom:12}}>
        <Field label={nameLabel||"Name"}>
          <input type="text" value={nameVal} onChange={e=>setNameVal(e.target.value)} placeholder="Full name" />
        </Field>
        <Field label="Date">
          <input type="date" value={dateVal} onChange={e=>setDateVal(e.target.value)} />
        </Field>
      </div>
      <Field label="Signature">
        <div className="wf-sig-box">
          {empty && <div className="wf-sig-hint">Sign here</div>}
          <import_SignatureCanvas ref={sigRef2} penColor={C.navy}
            canvasProps={{height:120,style:{width:"100%"}}}
            onBegin={()=>setEmpty(false)} />
          <div className="wf-sig-actions">
            <button className="btn btn-ghost" type="button"
              onClick={()=>{sigRef2.current?.clear();setEmpty(true);}}>Clear</button>
          </div>
        </div>
      </Field>
    </div>
  );

  // Use raw SignatureCanvas since we need multiple refs
  const SC = require("react-signature-canvas").default;

  return (
    <FormShell title="Consent for In-Office Vestibular Function Procedures"
      subtitle={`Thank you for choosing ${practiceName||"our practice"} for your care. Please read and sign before your test.`}
      dbLabel="MongoDB · vestibular_consent"
      patientName={patientName} patientAge={patientAge} patientGender={patientGender}
      savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">ℹ</span>About the VNG Test</div>
        <div className="wf-policy">
          <p>I authorize {providerName||practiceName||"the provider"} staff to perform <strong>Video Nystagmography (VNG testing)</strong>. VNG uses a lightweight goggle assembly with a small camera located in front of one eye to record eye movements during the test.</p>
          <p><strong>Benefit:</strong> To help determine the cause of dizziness and to prevent falls. Results will guide physical therapy or further neurological assessment.</p>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">✓</span>I Understand and Consent To</div>
        <div className="wf-policy">
          <ul>
            <li>I will be asked to perform tasks requiring body and eye movement, including blowing warm or cool air into my ears, and occasionally a half-teaspoon of ice water.</li>
            <li>Some tests may cause dizziness or disorientation. I have been made aware of this and understand the consequences.</li>
            <li>I am invited to stop the tests at any time I feel uncomfortable.</li>
            <li>Alternatives have been explained — specifically, treatment without the benefit of information gained by these tests.</li>
            <li>Common risks include dizziness, vomiting, increased blood pressure, incontinence (in the elderly), falls, sore neck muscles, and rarely irritation of the ear canal. Usually these are brief and self-limited.</li>
            <li>I may not be able to drive after the test, depending on how I feel afterwards.</li>
          </ul>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">✍</span>Patient / Responsible Party</div>
        <div className="wf-grid wf-grid-2" style={{marginBottom:16}}>
          <Field label="Name">
            <input type="text" value={form.responsibleParty} onChange={e=>set("responsibleParty",e.target.value)} placeholder="Full name" />
          </Field>
          <Field label="Date">
            <input type="date" value={form.sigDate} onChange={e=>set("sigDate",e.target.value)} />
          </Field>
        </div>
        <Field label="Signature (draw below)">
          <div className="wf-sig-box">
            {sigEmpty && <div className="wf-sig-hint">Sign here</div>}
            <SC ref={sigRef} penColor={C.navy} canvasProps={{height:150,style:{width:"100%"}}} onBegin={()=>setSigEmpty(false)} />
            <div className="wf-sig-actions">
              <button className="btn btn-ghost" type="button" onClick={()=>{sigRef.current?.clear();setSigEmpty(true);}}>Clear</button>
            </div>
          </div>
        </Field>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">W</span>Witness</div>
        <div className="wf-grid wf-grid-2" style={{marginBottom:16}}>
          <Field label="Witness Name">
            <input type="text" value={form.witnessName} onChange={e=>set("witnessName",e.target.value)} placeholder="Witness full name" />
          </Field>
          <Field label="Date">
            <input type="date" value={form.witnessDate} onChange={e=>set("witnessDate",e.target.value)} />
          </Field>
        </div>
        <Field label="Witness Signature">
          <div className="wf-sig-box">
            {witnessSigEmpty && <div className="wf-sig-hint">Witness signs here</div>}
            <SC ref={witnessSigRef} penColor={C.navy} canvasProps={{height:120,style:{width:"100%"}}} onBegin={()=>setWitnessSigEmpty(false)} />
            <div className="wf-sig-actions">
              <button className="btn btn-ghost" type="button" onClick={()=>{witnessSigRef.current?.clear();setWitnessSigEmpty(true);}}>Clear</button>
            </div>
          </div>
        </Field>
      </div>

      <div className="wf-section" style={{borderBottom:"none"}}>
        <div className="wf-section-title"><span className="num">T</span>Diagnostic Technician Declaration</div>
        <div className="wf-policy" style={{marginBottom:16}}>
          <p>I have explained these procedures/function testing risks, alternatives and expectations to the patient, and believe he/she has been adequately informed and has consented.</p>
        </div>
        <div className="wf-grid wf-grid-2" style={{marginBottom:16}}>
          <Field label="Technician Name">
            <input type="text" value={form.techName} onChange={e=>set("techName",e.target.value)} placeholder="Technician full name" />
          </Field>
          <Field label="Date">
            <input type="date" value={form.techDate} onChange={e=>set("techDate",e.target.value)} />
          </Field>
        </div>
        <Field label="Technician Signature">
          <div className="wf-sig-box">
            {techSigEmpty && <div className="wf-sig-hint">Technician signs here</div>}
            <SC ref={techSigRef} penColor={C.navy} canvasProps={{height:120,style:{width:"100%"}}} onBegin={()=>setTechSigEmpty(false)} />
            <div className="wf-sig-actions">
              <button className="btn btn-ghost" type="button" onClick={()=>{techSigRef.current?.clear();setTechSigEmpty(true);}}>Clear</button>
            </div>
          </div>
        </Field>
        <p style={{marginTop:12,fontSize:11,color:C.muted,lineHeight:1.5}}>
          Note: Patients are solely responsible for maintaining the privacy and security of all information printed from the Patient Portal.
        </p>
      </div>

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const VestibularSchema = new Schema({
  formType:     { type:String, default:"vestibular_consent" },
  patient:      { name:String, age:Number, gender:String },
  providerName: String, practiceName: String, consentGiven: Boolean,
  authorization:        { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  witness:              { name:String, date:Date, signatureDataURL:String },
  diagnosticTechnician: { name:String, date:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"vestibular_consent" });
*/
